import FilterListIcon from "@mui/icons-material/FilterList";
import { Box, Button, Chip, Link, Stack, Typography } from "@mui/material";
import { brand } from "../../../theme/themePrimitives";
import { Dayjs } from "dayjs";
import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  ColumnDef,
  DataTable,
  SortOrder,
} from "../../../components/DataTable/DataTable";
import SelectedItemsPreview from "../../../components/SelectedItemsPreview/SelectedItemsPreview";
import { usePageMetadata } from "../../../context/PageMetadataContext";
import {
  GetTrainingRecordResult,
  GetTrainingRecordsPaginatedResult,
} from "../../../services/api/types/editTraniningRecords/editTrainingRecords";
import { GetUserResult, Roles } from "../../../services/api/types/users/users";
import {
  AssignmentType,
  ProgressStatus,
} from "../../../services/api/types/general/homeAssignment";
import { Filter, FilterGroup } from "./components/Filter";
import { useAuth } from "../../../context/AuthContext";
import { getAllTrainingRecordsPaginated } from "../../../services/api/apiClient";

export const MANUAL_SELECTED_USERS_KEY = "manualSelectedUsers";
export const FILTERED_SELECTED_USERS_KEY = "filteredSelectedUsers";
export const SELECTED_USERS_SOURCE_KEY = "selectedUsersSource";
export const LEGACY_SELECTED_USERS_KEY = "selectedUsers";

const INITIAL_FILTERS: FilterGroup[] = [
  {
    title: "Status",
    items: Object.values(ProgressStatus).map((value) => ({
      id: value,
      name: value.replace(/-/g, " ").replace(/\(/g, " ("),
      checked: false,
    })),
  },
  {
    title: "Type",
    items: Object.values(AssignmentType).map((value) => ({
      id: value,
      name: value.charAt(0).toUpperCase() + value.slice(1),
      checked: false,
    })),
  },
];

function getSelectedUsersFromStorage(): GetUserResult[] {
  try {
    const parseUsers = (key: string): GetUserResult[] => {
      const stored = localStorage.getItem(key);
      if (!stored) return [];
      const parsed = JSON.parse(stored);
      return Array.isArray(parsed) ? parsed : [];
    };

    const source = localStorage.getItem(SELECTED_USERS_SOURCE_KEY) as
      | "manual"
      | "filtered"
      | null;

    const manualUsers = parseUsers(MANUAL_SELECTED_USERS_KEY);
    const filteredUsers = parseUsers(FILTERED_SELECTED_USERS_KEY);
    const legacyUsers = parseUsers(LEGACY_SELECTED_USERS_KEY);

    if (source === "filtered") {
      return filteredUsers.length ? filteredUsers : legacyUsers;
    }
    if (source === "manual") {
      return manualUsers;
    }
    if (filteredUsers.length) return filteredUsers;
    if (manualUsers.length) return manualUsers;
    return legacyUsers;
  } catch {
    return [];
  }
}

export const EditTrainingRecords = () => {
  const [showFilter, setShowFilter] = useState(false);
  const [filtersList, setFiltersList] =
    useState<FilterGroup[]>(INITIAL_FILTERS);
  const [appliedFiltersList, setAppliedFiltersList] =
    useState<FilterGroup[]>(INITIAL_FILTERS);
  const [appliedStartDateFrom, setAppliedStartDateFrom] =
    useState<Dayjs | null>(null);
  const [appliedStartDateTo, setAppliedStartDateTo] = useState<Dayjs | null>(
    null,
  );
  const [appliedCompletionDateFrom, setAppliedCompletionDateFrom] =
    useState<Dayjs | null>(null);
  const [appliedCompletionDateTo, setAppliedCompletionDateTo] =
    useState<Dayjs | null>(null);

  const [startDateFrom, setStartDateFrom] = useState<Dayjs | null>(null);
  const [startDateTo, setStartDateTo] = useState<Dayjs | null>(null);
  const [completionDateFrom, setCompletionDateFrom] = useState<Dayjs | null>(
    null,
  );
  const [completionDateTo, setCompletionDateTo] = useState<Dayjs | null>(null);

  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState<{ orderBy: string; order: SortOrder }>({
    orderBy: "title",
    order: "asc",
  });

  const { portalUid } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { auth } = useAuth();

  const isAdmin =
    (auth.user?.roles?.includes(Roles.SYSTEM_ADMIN) ||
      auth.user?.roles?.includes(Roles.MAIN_ADMIN) ||
      auth.user?.roles?.includes(Roles.PORTAL_ADMIN) ||
      auth.user?.roles?.includes(Roles.ADMIN)) ??
    false;

  const externalSelectedUsers: GetUserResult[] = useMemo(() => {
    const fromState = (location.state &&
      (location.state as any).selectedUsers) as GetUserResult[] | undefined;
    if (fromState && fromState.length > 0) {
      return fromState;
    }
    return getSelectedUsersFromStorage();
  }, [location.state]);

  const handleSearchChange = (value: string) => {
    setSearch(value);
    setPage(1);
  };

  const handleSortChange = (orderBy: string, order: SortOrder) => {
    setSort({ orderBy, order });
    setPage(1);
  };

  const handleRowsPerPageChange = (rows: number) => {
    setRowsPerPage(rows);
    setPage(1);
  };

  const handleCheckboxChange = (groupIndex: number, itemIndex: number) => {
    setFiltersList((prev) => {
      const copy = JSON.parse(JSON.stringify(prev));
      copy[groupIndex].items[itemIndex].checked =
        !copy[groupIndex].items[itemIndex].checked;
      return copy;
    });
  };

  const saveFilters = () => {
    setAppliedFiltersList(JSON.parse(JSON.stringify(filtersList)));
    setAppliedStartDateFrom(startDateFrom);
    setAppliedStartDateTo(startDateTo);
    setAppliedCompletionDateFrom(completionDateFrom);
    setAppliedCompletionDateTo(completionDateTo);
    setPage(1);
  };

  const clearFilters = () => {
    const cleared = INITIAL_FILTERS.map((g) => ({
      ...g,
      items: g.items.map((i) => ({ ...i, checked: false })),
    }));
    setFiltersList(cleared);
    setAppliedFiltersList(cleared);
    setStartDateFrom(null);
    setStartDateTo(null);
    setCompletionDateFrom(null);
    setCompletionDateTo(null);
    setAppliedStartDateFrom(null);
    setAppliedStartDateTo(null);
    setAppliedCompletionDateFrom(null);
    setAppliedCompletionDateTo(null);
    setPage(1);
  };

  const [trainingRecords, setTrainingRecords] =
    useState<GetTrainingRecordsPaginatedResult>();
  const [loading, setLoading] = useState(false);

  const fetchTrainingRecords = async () => {
    const userUid = externalSelectedUsers[0]?.UserPortalUid;
    if (!userUid || !portalUid) return;

    const checkedStatuses = appliedFiltersList
      .find((g) => g.title === "Status")
      ?.items.filter((i) => i.checked)
      .map((i) => i.id);

    const checkedTypes = appliedFiltersList
      .find((g) => g.title === "Type")
      ?.items.filter((i) => i.checked)
      .map((i) => i.id);
    setLoading(true);
    try {
      const data = await getAllTrainingRecordsPaginated(portalUid, {
        UserPortalUid: userUid,
        search: search || undefined,
        status: checkedStatuses?.join(",") || undefined,
        type: checkedTypes?.join(",") || undefined,
        startDateFrom: appliedStartDateFrom?.format("YYYY-MM-DD") || undefined,
        startDateTo: appliedStartDateTo?.format("YYYY-MM-DD") || undefined,
        complitionDateFrom:
          appliedCompletionDateFrom?.format("YYYY-MM-DD") || undefined,
        complitionDateTo:
          appliedCompletionDateTo?.format("YYYY-MM-DD") || undefined,
        limit: rowsPerPage,
        offset: (page - 1) * rowsPerPage,
        orderBy: sort.orderBy,
        sort: sort.order,
      });
      setTrainingRecords(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTrainingRecords();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    portalUid,
    externalSelectedUsers,
    page,
    rowsPerPage,
    sort,
    search,
    appliedFiltersList,
    appliedStartDateFrom,
    appliedStartDateTo,
    appliedCompletionDateFrom,
    appliedCompletionDateTo,
  ]);

  const appliedFilters = useMemo(
    () =>
      appliedFiltersList
        .flatMap((g) => g.items.map((i) => ({ group: g.title, ...i })))
        .filter((i) => i.checked),
    [appliedFiltersList],
  );

  const hasStartDateFilter = appliedStartDateFrom || appliedStartDateTo;
  const hasCompletionDateFilter =
    appliedCompletionDateFrom || appliedCompletionDateTo;
  const showFiltersBar =
    appliedFilters.length > 0 || hasStartDateFilter || hasCompletionDateFilter;

  const handleRemoveFilter = (groupTitle: string, itemId: string) => {
    const updated: FilterGroup[] = JSON.parse(
      JSON.stringify(appliedFiltersList),
    );
    const group = updated.find((g) => g.title === groupTitle);
    if (group) {
      const item = group.items.find((i) => i.id === itemId);
      if (item) item.checked = false;
    }
    setAppliedFiltersList(updated);
    setFiltersList(updated);
  };

  const handleRemoveStartDateFilter = () => {
    setAppliedStartDateFrom(null);
    setAppliedStartDateTo(null);
    setStartDateFrom(null);
    setStartDateTo(null);
  };

  const handleRemoveCompletionDateFilter = () => {
    setAppliedCompletionDateFrom(null);
    setAppliedCompletionDateTo(null);
    setCompletionDateFrom(null);
    setCompletionDateTo(null);
  };

  const formatDateRange = (from: Dayjs | null, to: Dayjs | null) =>
    [from?.format("DD MMM, YYYY"), to?.format("DD MMM, YYYY")]
      .filter(Boolean)
      .join(" - ");

  const columns: ColumnDef<GetTrainingRecordResult>[] = [
    {
      id: "title",
      label: "Title",
      sortable: true,
      render: (row) =>
        isAdmin ? (
          <Link
            component="button"
            underline="hover"
            sx={{
              color: "#7C3AED",
              fontWeight: 500,
              textAlign: "left",
              textDecoration: "none",
              "&::before": { display: "none" },
            }}
            onClick={() =>
              navigate(
                `/portal-configuration/${portalUid}/users/edit-training-records/${row.UserAssignmentUid}`,
                {
                  state: {
                    selectedUsers: externalSelectedUsers,
                    trainingRecord: row,
                  },
                },
              )
            }
          >
            {row.title}
          </Link>
        ) : (
          row.title
        ),
      getValue: (row) => row.title,
    },
    {
      id: "status",
      label: "Status",
      sortable: true,
      getValue: (row) => row.status,
    },
    {
      id: "type",
      label: "Type",
      sortable: true,
      getValue: (row) => row.type,
    },
    {
      id: "startDate",
      label: "Start Date",
      sortable: true,
      getValue: (row) => row.startDate,
    },
    {
      id: "completionDate",
      label: "Completion Date",
      sortable: true,
      getValue: (row) => row.completionDate,
    },
    {
      id: "score",
      label: "Score",
      sortable: true,
      getValue: (row) => row.score,
    },
  ];

  usePageMetadata(
    {
      title: "Edit training records",
      breadcrumbs: [
        {
          label: "User management",
          path: `/portal-configuration`,
        },
        {
          label: "User search",
          path: `/portal-configuration/${portalUid}/users`,
        },
        {
          label: "Edit training records",
        },
      ],
    },
    [portalUid],
  );

  return (
    <>
      <SelectedItemsPreview
        items={externalSelectedUsers.map((user) => ({
          id: user.UserPortalUid,
          title: `${user.firstName} ${user.lastName}`,
          subtitle: user.email,
        }))}
        itemType="users"
        previewTitle={
          externalSelectedUsers[0]?.firstName +
          " " +
          externalSelectedUsers[0]?.lastName
        }
        previewDescription={externalSelectedUsers[0]?.email ?? ""}
        showViewDetailsButton={false}
      />
      <DataTable
        data={trainingRecords?.rows || []}
        columns={columns}
        totalCount={trainingRecords?.count ?? 0}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={setPage}
        onRowsPerPageChange={handleRowsPerPageChange}
        sort={sort}
        isLoading={loading}
        onSortChange={handleSortChange}
        search={search}
        onSearchChange={handleSearchChange}
        searchPlaceholder="Search by title"
        getRowId={(row) => row.UserAssignmentUid}
        headerContent={
          showFiltersBar ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                py: 1,
              }}
            >
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                flexWrap="wrap"
              >
                <Typography sx={{ fontWeight: 600, whiteSpace: "nowrap" }}>
                  Filter applied:
                </Typography>
                {appliedFilters.map((f) => (
                  <Chip
                    key={f.id}
                    label={`${f.group}: ${f.name}`}
                    size="medium"
                    variant="outlined"
                    onDelete={() => handleRemoveFilter(f.group, f.id)}
                  />
                ))}
                {hasStartDateFilter && (
                  <Chip
                    label={`Start Date: ${formatDateRange(appliedStartDateFrom, appliedStartDateTo)}`}
                    size="medium"
                    variant="outlined"
                    onDelete={handleRemoveStartDateFilter}
                  />
                )}
                {hasCompletionDateFilter && (
                  <Chip
                    label={`Completion Date: ${formatDateRange(appliedCompletionDateFrom, appliedCompletionDateTo)}`}
                    size="medium"
                    variant="outlined"
                    onDelete={handleRemoveCompletionDateFilter}
                  />
                )}
              </Stack>
              <Button
                size="small"
                variant="text"
                sx={{
                  color: brand[600],
                  fontSize: "16px",
                  fontWeight: 600,
                  backgroundColor: "transparent !important",
                  whiteSpace: "nowrap",
                }}
                onClick={clearFilters}
              >
                Clear all
              </Button>
            </Box>
          ) : undefined
        }
        actions={
          <Button
            id="edit-training-records-filter-button"
            variant="outlined"
            startIcon={<FilterListIcon />}
            size="small"
            onClick={() => setShowFilter(!showFilter)}
          >
            Filter
          </Button>
        }
      />
      <Filter
        filtersList={filtersList}
        showFilter={showFilter}
        setShowFilter={setShowFilter}
        handleCheckboxChange={handleCheckboxChange}
        saveFilters={saveFilters}
        clearFilters={clearFilters}
        startDateFrom={startDateFrom}
        startDateTo={startDateTo}
        onStartDateFromChange={setStartDateFrom}
        onStartDateToChange={setStartDateTo}
        completionDateFrom={completionDateFrom}
        completionDateTo={completionDateTo}
        onCompletionDateFromChange={setCompletionDateFrom}
        onCompletionDateToChange={setCompletionDateTo}
      />
    </>
  );
};
